// Deterministic analysis of a drawing register: discipline breakdown, duplicate
// sheet numbers, numbering gaps within a series, and incomplete entries. Exact
// and offline; an optional AI pass (in the UI) adds qualitative coordination
// review on top.

import type { DrawingRef } from "./types";

export interface DisciplineCount {
  name: string;
  count: number;
}
export interface SeriesGap {
  series: string;
  present: number;
  missing: string[];
}
export interface DrawingAnalysis {
  total: number;
  byDiscipline: DisciplineCount[];
  withoutTitle: number;
  withoutRevision: number;
  unclassified: number;
  duplicates: string[]; // "A-101 (×2)"
  gaps: SeriesGap[];
  notInSeries: number;
  notes: string[]; // human-readable flags, most important first
}

/** Standard "prefix + number" sheet like A-101 / EL301 / M-4 (not the running
 *  project-prefixed style like 13-GUR-G9). Returns the series key + number. */
function seriesOf(number: string): { series: string; num: number; band: number } | null {
  const m = number.trim().match(/^([A-Za-z]{1,5})[-. ]?0*(\d{1,4})[A-Za-z]?$/);
  if (!m) return null;
  const num = parseInt(m[2], 10);
  return { series: m[1].toUpperCase(), num, band: Math.floor(num / 100) };
}

export function analyzeDrawings(drawings: DrawingRef[]): DrawingAnalysis {
  const total = drawings.length;

  // Discipline breakdown
  const discMap = new Map<string, number>();
  let unclassified = 0;
  for (const d of drawings) {
    const name = d.discipline?.trim() || "Unclassified";
    if (name === "Unclassified") unclassified++;
    discMap.set(name, (discMap.get(name) || 0) + 1);
  }
  const byDiscipline = [...discMap.entries()].map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count);

  const withoutTitle = drawings.filter((d) => !d.title?.trim()).length;
  const withoutRevision = drawings.filter((d) => !d.revision?.trim()).length;

  // Duplicate sheet numbers (case-insensitive)
  const numMap = new Map<string, { label: string; count: number }>();
  for (const d of drawings) {
    const key = (d.number || "").trim().toLowerCase();
    if (!key) continue;
    const e = numMap.get(key) || { label: d.number.trim(), count: 0 };
    e.count++;
    numMap.set(key, e);
  }
  const duplicates = [...numMap.values()].filter((e) => e.count > 1).map((e) => `${e.label} (×${e.count})`);

  // Numbering gaps within each series band
  const bands = new Map<string, number[]>();
  let notInSeries = 0;
  for (const d of drawings) {
    const s = seriesOf(d.number || "");
    if (!s) {
      if ((d.number || "").trim()) notInSeries++;
      continue;
    }
    const key = `${s.series}|${s.band}`;
    const arr = bands.get(key) || [];
    arr.push(s.num);
    bands.set(key, arr);
  }
  const gaps: SeriesGap[] = [];
  for (const [key, nums] of bands) {
    if (nums.length < 2) continue;
    const [series] = key.split("|");
    const sorted = [...new Set(nums)].sort((a, b) => a - b);
    const missing: string[] = [];
    for (let n = sorted[0] + 1; n < sorted[sorted.length - 1]; n++) {
      if (!sorted.includes(n)) missing.push(`${series}-${n}`);
    }
    if (missing.length && missing.length <= 12) gaps.push({ series, present: sorted.length, missing });
  }

  // Notes, most important first
  const notes: string[] = [];
  if (duplicates.length) notes.push(`Duplicate sheet number${duplicates.length > 1 ? "s" : ""}: ${duplicates.join(", ")}.`);
  for (const g of gaps) notes.push(`Possible gap in the ${g.series} series — missing ${g.missing.join(", ")}.`);
  if (withoutTitle) notes.push(`${withoutTitle} sheet${withoutTitle > 1 ? "s have" : " has"} no title.`);
  if (unclassified) notes.push(`${unclassified} sheet${unclassified > 1 ? "s" : ""} could not be classified by discipline.`);
  if (withoutRevision) notes.push(`${withoutRevision} sheet${withoutRevision > 1 ? "s have" : " has"} no revision noted.`);
  if (!total) notes.push("The drawing register is empty.");
  if (!notes.length) notes.push("No issues detected — the register looks complete and consistent.");

  return { total, byDiscipline, withoutTitle, withoutRevision, unclassified, duplicates, gaps, notInSeries, notes };
}

/** Compact text of the register for feeding an AI coordination review. */
export function registerToText(drawings: DrawingRef[]): string {
  return drawings.map((d) => `${d.number || "—"} | ${d.title || "—"}${d.revision ? ` | Rev ${d.revision}` : ""}${d.discipline ? ` | ${d.discipline}` : ""}`).join("\n");
}
